import React, { useState, useContext, useCallback, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { Grid, Typography, IconButton, Divider, styled } from '@mui/material'
import { emptyPromise, createProject, getProject, updateProject } from '../api'
import {
    AddIcon,
    DeleteIcon,
    EditIcon,
    ProjectsIcon,
    Surface,
    useS3Client,
    Retrievable,
    FormActions,
} from '../components'
import { FeedbackContext } from '../feedback'
import { translate } from '../i18n'
import { RouterParams } from '../router'
import {
    ImageTarget,
    isEquals,
    Project,
    ProjectRequest,
    ProjectSplash,
    PROJECT_ORIENTATIONS,
    PROJECT_TYPES,
} from '../model'
import ImageTargetForm from './ImageTargetForm'
import { generateMind } from './ImageTargetCompiler'
import { processImageTargetBatch, toPublicUrl } from './ImageTargetProcessor'
import ProjectImageTargets from './ProjectImageTargets'
import ProjectMenu from './ProjectMenu'
import ProjectPreviewPopup from './ProjectPreviewPopup'
import ImageTargetDeletePopup from './ImageTargetDeletePopup'
import SplashScreenForm from './SplashScreenForm'
import ProjectSplashScreen from './ProjectSplashScreen'
import ProjectDeployPopup from './ProjectDeployPopup'
import {
    AnyAutocomplete,
    AnyInput,
    AutocompleteOption,
    useCalculatedId,
    useValidatedForm,
} from '../anyform'
import { AppContext } from '../context'
import ProjectMobileBanner from './ProjectMobileBanner'

const Image = styled('img')(({ theme }) => ({
    position: 'fixed',
    top: '-5000px',
    visibility: 'hidden',
}))

type Popup =
    | 'add'
    | 'delete'
    | 'edit'
    | 'preview'
    | 'domain'
    | 'splash'
    | 'deploy'
    | 'qr'

export default function ProjectForm({ match }: RouterParams) {
    const history = useHistory()
    const feedbackContext = useContext(FeedbackContext)
    const context = useContext(AppContext)
    const tenantId = useMemo(() => context.session?.tenant?.id, [context])

    const projectId = match.params.projectId
    const paramTenantId = match.params.tenantId

    const s3Client = useS3Client()
    const { isValid, bind, register, registerInner, clearInner } =
        useValidatedForm<ProjectRequest>()

    const [popup, setPopup] = useState<Popup>()
    const [project, setProject] = useState<Project>()
    const [imageTargets, setImageTargets] = useState<ImageTarget[]>([])
    const [projectSplash, setProjectSplash] = useState<ProjectSplash>()

    const [imageTarget, setImageTarget] = useState<ImageTarget>()
    const [index, setIndex] = useState(-1)

    const { initialId, validators, hasChangedName } = useCalculatedId(projectId)
    const initialImageTargets = useMemo(
        () => project?.content?.dev?.image_targets ?? [],
        [project]
    )
    const withWebTarget = useMemo(() => project?.with_web_target, [project])

    const orientations = useMemo<AutocompleteOption[]>(
        () =>
            PROJECT_ORIENTATIONS.map((el) => ({
                value: el,
                label: translate(`projects.orientation.${el}`) as string,
            })),
        []
    )
    const types = useMemo<AutocompleteOption[]>(
        () =>
            PROJECT_TYPES.map((el) => ({
                value: el,
                label: translate(`projects.types.${el}`) as string,
            })),
        []
    )

    const retrievePromise = useCallback((): Promise<Project | undefined> => {
        if (projectId) {
            return getProject(projectId, tenantId)
        }
        return emptyPromise()
    }, [projectId, tenantId])

    const setData = useCallback((project?: Project) => {
        setProject(project)
        setImageTargets(project?.content?.dev?.image_targets ?? [])
        setProjectSplash(project?.content?.dev?.splash)
    }, [])

    const submitPromise = useCallback(
        ({ mindUrl, legacyMindUrl }: { mindUrl: string; legacyMindUrl: string }): Promise<Project> => {
            const { image, banner, ...request } = bind() as any
            const data = {
                ...request,
                tenant_id: paramTenantId,
                with_web_target: withWebTarget,
                stage_data: {
                    mind: mindUrl,
                    legacy_mind: legacyMindUrl,
                    image_targets: imageTargets,
                    splash: projectSplash,
                    banner: banner?.enabled ? banner : undefined,
                },
            }

            if (projectId) {
                return updateProject(projectId, data, tenantId)
            }
            return createProject(data, tenantId)
        },
        [
            bind,
            projectId,
            imageTargets,
            projectSplash,
            tenantId,
            paramTenantId,
            withWebTarget,
        ]
    )

    const submit = useCallback(
        (callback: (project: Project) => any) => {
            if (!isValid()) {
                return
            }

            const domTargets = document.querySelectorAll('[data-ar-content]')
            if (domTargets.length === 0) {
                feedbackContext.showWarning(
                    translate('projects.missing_images') as string
                )
                return
            }

            // When editing, projectId comes from the route. When creating, read
            // the id the user entered in the form.
            const currentProjectId = projectId ?? (bind() as any).id as string
            const sourceUrls = imageTargets.map(t => {
                const src = t.image2 ?? t.image
                return src.startsWith('http') ? src : toPublicUrl(src)
            })

            feedbackContext.showBackdrop(
                translate('projects.creating_mind') as string
            )

            Promise.all([
                generateMind(domTargets),
                processImageTargetBatch(sourceUrls, currentProjectId),
            ])
                .then(async ([mindBlob, xrResult]) => {
                    feedbackContext.showBackdrop(
                        translate('projects.uploading_mind') as string
                    )

                    const legacyMindKey = s3Client.calculateKey(
                        'mind-files',
                        'targets.zip',
                        true
                    )

                    await Promise.all([
                        s3Client.upload(legacyMindKey, mindBlob),
                        ...xrResult.targets.map(t =>
                            s3Client.upload(t.luminanceKey, t.luminanceBlob)
                        ),
                        s3Client.upload(xrResult.targetsJsonKey, xrResult.targetsJsonBlob),
                    ])

                    return { legacyMindKey, mindKey: xrResult.targetsJsonKey }
                })
                .then(({ legacyMindKey, mindKey }) => {
                    feedbackContext.showBackdrop(
                        translate('projects.updating') as string
                    )
                    return submitPromise({ mindUrl: mindKey, legacyMindUrl: legacyMindKey })
                })
                .then(callback)
                .catch((error) => feedbackContext.showError(error.message))
                .finally(feedbackContext.closeBackdrop)
        },
        [feedbackContext, s3Client, isValid, submitPromise, bind, projectId, imageTargets]
    )

    const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        submit((project) => {
            feedbackContext.showSuccess(
                translate(`projects.${projectId ? 'edited' : 'created'}`, {
                    name: project.name,
                }) as string
            )

            if (!projectId) {
                history.replace(
                    `${
                        paramTenantId ? `/tenants/${paramTenantId}` : ''
                    }/projects/${project.id}/edit`
                )
            }
        })
    }

    const addImageTarget = useCallback(() => {
        setIndex(-1)
        setPopup('add')
    }, [])

    const closePopup = useCallback(() => {
        setIndex(-1)
        setPopup(undefined)
        setImageTarget(undefined)
    }, [])

    const updateImageTarget = useCallback(
        (imageTarget: ImageTarget) => {
            setImageTargets((curr) => {
                if (index < 0) {
                    return [...curr, imageTarget]
                }

                return curr.map((el, i) => {
                    if (i === index) {
                        return imageTarget
                    }
                    return el
                })
            })
            closePopup()
        },
        [closePopup, index]
    )

    const deleteImageTarget = useCallback((index: number) => {
        setIndex(index)
        setPopup('delete')
    }, [])

    const editImageTarget = useCallback(
        (index: number) => {
            setImageTarget(imageTargets[index])
            setIndex(index)
            setPopup('edit')
        },
        [imageTargets]
    )

    const onDelete = useCallback(() => {
        setImageTargets((curr) => {
            return curr.filter((el, i) => {
                return i !== index
            })
        })
        closePopup()
    }, [index, closePopup])

    const handleChanges = useCallback(
        (popup: Popup, message: string) => {
            if (isEquals(initialImageTargets, imageTargets)) {
                setPopup(popup)
            } else {
                if (!isValid()) {
                    feedbackContext.showWarning(translate(message) as string)
                    return
                }

                submit((project) => {
                    setProject(project)
                    setPopup(popup)
                })
            }
        },
        [initialImageTargets, feedbackContext, imageTargets, isValid, submit]
    )

    const onPreview = useCallback(() => {
        handleChanges('preview', 'projects.dev.bad_request')
    }, [handleChanges])

    const onQr = useCallback(() => {
        setPopup('qr')
    }, [])

    const onDeploy = useCallback(() => {
        handleChanges('deploy', 'projects.deploy.bad_request')
    }, [handleChanges])

    const onSplashScreen = useCallback(() => {
        setPopup('splash')
    }, [])

    const onUpdatedSplashScreen = useCallback((data?: ProjectSplash) => {
        setPopup(undefined)
        setProjectSplash(data)
    }, [])

    return (
        <Retrievable
            getPromise={retrievePromise}
            setData={setData}
            builder={() => (
                <Grid item xs={12}>
                    <Grid container justifyContent="center" alignItems="center">
                        <Grid item xs={12} md={10} lg={7} xl={5}>
                            <Surface
                                title={translate(
                                    projectId ? 'projects.edit' : 'projects.new'
                                )}
                                icon={<ProjectsIcon />}
                                ellipsisBuilder={
                                    project
                                        ? () => (
                                              <ProjectMenu
                                                  paramTenantId={paramTenantId}
                                                  project={project}
                                                  variant="form"
                                                  onPreview={onPreview}
                                                  onQr={onQr}
                                                  onDeploy={onDeploy}
                                                  onDelete={() => {}}
                                              />
                                          )
                                        : undefined
                                }
                            >
                                <form
                                    autoComplete="off"
                                    noValidate
                                    onSubmit={onSubmit}
                                >
                                    <Grid
                                        container
                                        justifyContent="space-between"
                                        alignItems="center"
                                    >
                                        <Grid item xs={12}>
                                            <AnyInput
                                                type="text"
                                                name="name"
                                                initialValue={project?.name}
                                                label={
                                                    translate(
                                                        'projects.name'
                                                    ) as string
                                                }
                                                required
                                                register={register}
                                                notifier={hasChangedName}
                                            />
                                            <AnyInput
                                                type="text"
                                                name="id"
                                                initialValue={initialId}
                                                label={
                                                    translate(
                                                        'projects.id'
                                                    ) as string
                                                }
                                                required
                                                disabled={!!projectId}
                                                validators={validators}
                                                register={register}
                                            />
                                        </Grid>
                                        <Grid
                                            item
                                            xs={12}
                                            sx={{ display: 'none' }}
                                        >
                                            <AnyAutocomplete
                                                name="orientation"
                                                initialValue={
                                                    project?.orientation ??
                                                    'PORTRAIT'
                                                }
                                                options={orientations}
                                                label={
                                                    translate(
                                                        'projects.orientation.title'
                                                    ) as string
                                                }
                                                required
                                                register={register}
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <AnyAutocomplete
                                                name="type"
                                                initialValue={project?.type}
                                                options={types}
                                                label={
                                                    translate(
                                                        'projects.types.title'
                                                    ) as string
                                                }
                                                required
                                                register={register}
                                            />
                                            <AnyInput
                                                type="text"
                                                name="description"
                                                initialValue={
                                                    project?.description
                                                }
                                                label={
                                                    translate(
                                                        'projects.description'
                                                    ) as string
                                                }
                                                minRows={2}
                                                maxRows={4}
                                                register={register}
                                            />
                                        </Grid>

                                        <Grid item xs={12} sx={{ mt: 2 }}>
                                            <Grid
                                                container
                                                justifyContent="center"
                                                justifyItems="center"
                                                alignContent="center"
                                                alignItems="center"
                                            >
                                                <Grid item xs>
                                                    <Typography variant="subtitle1">
                                                        {translate(
                                                            'projects.image_targets.title'
                                                        )}
                                                    </Typography>
                                                </Grid>
                                                <Grid item xs="auto">
                                                    <IconButton
                                                        color="primary"
                                                        size="large"
                                                        onClick={addImageTarget}
                                                    >
                                                        <AddIcon />
                                                    </IconButton>
                                                </Grid>
                                            </Grid>
                                            <Divider />
                                            <ProjectImageTargets
                                                imageTargets={imageTargets}
                                                onEdit={editImageTarget}
                                                onDelete={deleteImageTarget}
                                            />
                                            <Divider sx={{ mb: 1 }} />
                                        </Grid>

                                        <Grid item xs={12} sx={{ mt: 2 }}>
                                            <Grid
                                                container
                                                justifyContent="center"
                                                justifyItems="center"
                                                alignContent="center"
                                                alignItems="center"
                                            >
                                                <Grid item xs>
                                                    <Typography variant="subtitle1">
                                                        {translate(
                                                            'projects.splash.title'
                                                        )}
                                                    </Typography>
                                                </Grid>
                                                {projectSplash && (
                                                    <Grid item xs="auto">
                                                        <IconButton
                                                            color="error"
                                                            size="large"
                                                            onClick={() =>
                                                                onUpdatedSplashScreen(
                                                                    undefined
                                                                )
                                                            }
                                                        >
                                                            <DeleteIcon />
                                                        </IconButton>
                                                    </Grid>
                                                )}
                                                <Grid item xs="auto">
                                                    <IconButton
                                                        color="primary"
                                                        size="large"
                                                        onClick={onSplashScreen}
                                                    >
                                                        {projectSplash ? (
                                                            <EditIcon />
                                                        ) : (
                                                            <AddIcon />
                                                        )}
                                                    </IconButton>
                                                </Grid>
                                            </Grid>
                                            <Divider />
                                            <ProjectSplashScreen
                                                splash={projectSplash}
                                            />
                                            <Divider sx={{ mb: 1 }} />
                                        </Grid>

                                        <ProjectMobileBanner
                                            banner={
                                                project?.content?.dev?.banner
                                            }
                                            registerInner={registerInner}
                                            clearInner={clearInner}
                                        />

                                        <FormActions
                                            id={project?.id}
                                            disabled={imageTargets.length === 0}
                                        />
                                    </Grid>
                                </form>
                                {(popup === 'add' || popup === 'edit') && (
                                    <ImageTargetForm
                                        imageTarget={imageTarget}
                                        s3Client={s3Client}
                                        withWebTarget={withWebTarget}
                                        onUpdated={updateImageTarget}
                                        onClose={closePopup}
                                    />
                                )}
                                {popup === 'delete' && (
                                    <ImageTargetDeletePopup
                                        onDelete={onDelete}
                                        onClose={closePopup}
                                    />
                                )}
                                {project && popup === 'preview' && (
                                    <ProjectPreviewPopup
                                        stage="dev"
                                        project={project}
                                        onClose={closePopup}
                                    />
                                )}
                                {project && popup === 'qr' && (
                                    <ProjectPreviewPopup
                                        stage="prod"
                                        project={project}
                                        onClose={closePopup}
                                    />
                                )}
                                {project && popup === 'deploy' && (
                                    <ProjectDeployPopup
                                        project={project}
                                        onDeployed={setProject}
                                        onClose={closePopup}
                                    />
                                )}
                                {popup === 'splash' && (
                                    <SplashScreenForm
                                        current={projectSplash}
                                        s3Client={s3Client}
                                        onUpdated={onUpdatedSplashScreen}
                                        onClose={closePopup}
                                    />
                                )}
                            </Surface>
                        </Grid>
                    </Grid>
                    {imageTargets
                        .map((el) => el.image2 ?? el.image)
                        .map((src) => (
                            <Image
                                key={src}
                                src={src}
                                alt=""
                                crossOrigin="anonymous"
                                data-ar-content
                            />
                        ))}
                </Grid>
            )}
        />
    )
}
